import {
  defaultMutationFn,
  defaultRawMutationFn,
  defaultTextPlainMutationFn,
} from 'api';
import { useMutation, useQueryClient } from 'react-query';

export function useDeleteNote() {
  const queryClient = useQueryClient();

  type DeleteNoteData = { vehicleId: string; noteId: string };
  const mutation = useMutation(({ vehicleId, noteId }: DeleteNoteData) => {
    const path = `/inventory/${vehicleId}/notes/${noteId}`;
    return defaultMutationFn(path, 'DELETE');
  });

  async function deleteNoteAsync({ vehicleId, noteId }: DeleteNoteData) {
    const response = await mutation.mutateAsync({ vehicleId, noteId });
    await queryClient.invalidateQueries(`/inventory/${vehicleId}/notes`);
    return response;
  }

  return {
    ...mutation,
    deleteNoteAsync,
  };
}

export function useDeleteNoteAttachment() {
  const queryClient = useQueryClient();

  type NoteAttachmentData = {
    vehicleId: string;
    noteId: string;
    attachmentId: string;
  };
  const mutation = useMutation(
    ({ vehicleId, noteId, attachmentId }: NoteAttachmentData) => {
      const path = `/inventory/${vehicleId}/notes/${noteId}/attachments/${attachmentId}`;
      return defaultMutationFn(path, 'DELETE');
    }
  );

  async function deleteNoteAttachmentAsync({
    vehicleId,
    noteId,
    attachmentId,
  }: NoteAttachmentData) {
    const response = await mutation.mutateAsync({
      vehicleId,
      noteId,
      attachmentId,
    });
    await queryClient.invalidateQueries(`/inventory/${vehicleId}/notes`);
    return response;
  }

  return {
    ...mutation,
    deleteNoteAttachmentAsync,
  };
}

export function useUpdateNote() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      vehicleId,
      noteId,
      note,
    }: {
      vehicleId: string;
      noteId: string;
      note: string;
    }) => {
      const path = `/inventory/${vehicleId}/notes/${noteId}`;
      return defaultRawMutationFn(path, 'PUT', note);
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          `/inventory/${variables.vehicleId}/notes`
        );
      },
    }
  );
}

export function useAddNote() {
  type AddNoteData = { vehicleId: string; note: string };
  const queryClient = useQueryClient();
  const mutation = useMutation(({ vehicleId, note }: AddNoteData) => {
    const path = `/inventory/${vehicleId}/notes`;
    return defaultTextPlainMutationFn(path, 'POST', note);
  });

  async function addNoteAsync({
    vehicleId,
    note,
  }: AddNoteData): Promise<{ data: { id: string } }> {
    const response = await mutation.mutateAsync({ vehicleId, note });
    await queryClient.invalidateQueries(`/inventory/${vehicleId}/notes`);
    return response;
  }

  return {
    ...mutation,
    addNoteAsync,
  };
}
